// EN-4743. The PDF417 decode steps, shared by the Web Worker (pdf417.worker.ts)
// and the page (pdf417Reader.ts, when a worker can't be used). No DOM access
// here beyond the canvas it is given, so it runs in either place.
//
// Plain decoders fail on a card held a few degrees off level, so a region can be
// tried at small correction angles, and small regions are upscaled first. In the
// EN-4743 benchmark this took zxing-wasm from 10/16 to 15/16.
//
// Accuracy (plan 07): the camera now streams at up to 4K so the bars stay
// readable at hand distance. The guide area is decoded at up to MAX_DECODE_WIDTH
// (keeps >= 2 px per bar without decoding a whole 4K frame), the whole frame at up
// to WHOLE_FRAME_MAX_WIDTH, and hard cards (dust, wear, uneven light) get other
// binarizer / denoise variants.
import type { ReaderOptions } from 'zxing-wasm/reader';
import type { Region } from '../../utils/mrz/mrzImage';

// textMode 'Plain' is required. The default ('HRI') rewrites control characters
// as <LF>, <RS>, <CR>, which breaks every AAMVA payload.
export const READ_OPTIONS: ReaderOptions = {
  formats: ['PDF417'],
  tryHarder: true,
  tryRotate: true,
  textMode: 'Plain',
  maxNumberOfSymbols: 1,
};

/**
 * Decoder variants, tried in rotation:
 *   A  zxing's default local-average binarizer: most cards
 *   B  global histogram binarizer, after a contrast stretch: faded print, uneven light
 *   C  denoise (morphological closing), after a contrast stretch: dust, specks, worn bars
 */
export type DecodeVariant = 'A' | 'B' | 'C';
export const READ_VARIANTS: Record<DecodeVariant, ReaderOptions> = {
  A: READ_OPTIONS,
  B: { ...READ_OPTIONS, binarizer: 'GlobalHistogram' },
  C: { ...READ_OPTIONS, tryDenoise: true },
};
export const ALL_VARIANTS: readonly DecodeVariant[] = ['A', 'B', 'C'];

// Correction angles, level first, then outward in 3 degree steps.
export const SWEEP_ANGLES = [0, -3, 3, -6, 6, -9, 9, -12, 12, -15, 15, -18, 18, -21, 21];
// Regions narrower than this are also tried upscaled. Below ~1.5 px per barcode
// module the decoder misses; upscaling fixed that in every test.
export const MIN_DECODE_WIDTH = 1200;
// The guide area is decoded at up to this width: at 4K the guide is ~3450 px, and
// 2400 still keeps a 0.25 mm bar at >= 2 px from ~25 cm while roughly halving the
// work per try.
export const MAX_DECODE_WIDTH = 2400;
// Time budget for the last-chance read of the photo (3rd miss), so the send isn't
// held up for long on a card that won't read: a readable card is normally read in
// the first few tries. Tunable from id_scan_summary (photoReadMs, outcome).
export const PHOTO_READ_BUDGET_MS = 2500;
// The whole frame only matters for a card held close enough to overflow the
// guide, where the bars are large, so it's decoded smaller.
export const WHOLE_FRAME_MAX_WIDTH = 1920;

/** One region of the source image, tried at each angle in turn. */
export interface DecodeTask {
  region: Region;
  angles: readonly number[];
  variant?: DecodeVariant;
  // Largest width to decode this region at; bigger regions are scaled down.
  maxWidth?: number;
  // Narrower regions are also tried scaled up to this width (default
  // MIN_DECODE_WIDTH). The live guide uses MAX_DECODE_WIDTH: on a 1080p stream,
  // enlarging it to 2400 px took a clean card from 15 cm to 25 cm in the bench.
  minWidth?: number;
}

/** One image handed to the decoder. */
export interface DecodeTry {
  region: Region;
  scale: number;
  angle: number;
  variant: DecodeVariant;
}

const scalesFor = (width: number, maxWidth: number, minWidth: number): number[] => {
  if (width > maxWidth) return [maxWidth / width];
  return width < minWidth ? [1, minWidth / width] : [1];
};

/**
 * Every image to try, in order: task by task, angle by angle, and at the
 * original size before the upscaled one (upscaling made a noisy frame worse in
 * testing). Empty regions are skipped.
 */
export const planTries = (tasks: readonly DecodeTask[]): DecodeTry[] =>
  tasks.flatMap(({ region, angles, variant = 'A', maxWidth = Infinity, minWidth = MIN_DECODE_WIDTH }) => {
    if (region.w < 1 || region.h < 1) return [];
    const scales = scalesFor(region.w, maxWidth, Math.min(minWidth, maxWidth));
    return angles.flatMap((angle) => scales.map((scale) => ({ region, scale, angle, variant })));
  });

type Canvas2D = HTMLCanvasElement | OffscreenCanvas;

/**
 * Stretches the grey levels between the 2nd and 98th percentile to the full
 * range, in place. Faded or dusty bars get their contrast back before
 * binarizing. Colour is kept (zxing converts to luminance itself).
 */
export const stretchContrast = (image: ImageData): ImageData => {
  const d = image.data;
  const histogram = new Uint32Array(256);
  // Sample every 4th pixel: plenty for percentiles, a quarter of the work.
  let samples = 0;
  for (let i = 0; i < d.length; i += 16) {
    histogram[(d[i] * 77 + d[i + 1] * 150 + d[i + 2] * 29) >> 8] += 1;
    samples += 1;
  }
  const cut = samples * 0.02;
  let lo = 0;
  for (let acc = 0; lo < 255 && acc + histogram[lo] <= cut; lo += 1) acc += histogram[lo];
  let hi = 255;
  for (let acc = 0; hi > 0 && acc + histogram[hi] <= cut; hi -= 1) acc += histogram[hi];
  if (hi - lo < 16) return image; // flat image: nothing to stretch
  const lut = new Uint8ClampedArray(256);
  for (let v = 0; v < 256; v += 1) lut[v] = ((v - lo) * 255) / (hi - lo);
  for (let i = 0; i < d.length; i += 4) {
    d[i] = lut[d[i]];
    d[i + 1] = lut[d[i + 1]];
    d[i + 2] = lut[d[i + 2]];
  }
  return image;
};

/**
 * Draws one try into `canvas`: the region of `source`, scaled and rotated about
 * its centre on a grey background, and returns its pixels (contrast-stretched
 * for the B / C variants).
 */
export const drawTry = (canvas: Canvas2D, source: CanvasImageSource, t: DecodeTry): ImageData | null => {
  const { region, scale, angle, variant } = t;
  const ctx = canvas.getContext('2d', { willReadFrequently: true }) as
    | CanvasRenderingContext2D
    | OffscreenCanvasRenderingContext2D
    | null;
  if (!ctx) return null;
  const w = Math.max(1, Math.round(region.w * scale));
  const h = Math.max(1, Math.round(region.h * scale));
  const a = (angle * Math.PI) / 180;
  const cw = Math.ceil(Math.abs(w * Math.cos(a)) + Math.abs(h * Math.sin(a)));
  const ch = Math.ceil(Math.abs(w * Math.sin(a)) + Math.abs(h * Math.cos(a)));
  canvas.width = cw;
  canvas.height = ch;
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.fillStyle = '#808080';
  ctx.fillRect(0, 0, cw, ch);
  ctx.translate(cw / 2, ch / 2);
  ctx.rotate(a);
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(source, region.x, region.y, region.w, region.h, -w / 2, -h / 2, w, h);
  const image = ctx.getImageData(0, 0, cw, ch);
  return variant === 'A' ? image : stretchContrast(image);
};

export interface DecodeOutcome {
  text: string | null;
  tries: number;
  // The variant that read it, for telemetry. Null on a miss.
  variant: DecodeVariant | null;
}

/**
 * Runs the tries until one decodes. `render` makes a try's pixels; `read`
 * decodes them (in this thread or another) with the try's variant. A failed try
 * is just a miss. With a `deadline` (a performance.now() time), no new try
 * starts after it: the tries are ordered most useful first, so a budget keeps the
 * likely reads and drops the long tail.
 */
export const decodeTries = async (
  tries: readonly DecodeTry[],
  render: (t: DecodeTry) => ImageData | null,
  read: (image: ImageData, variant: DecodeVariant) => Promise<string | null>,
  deadline = Infinity,
): Promise<DecodeOutcome> => {
  let count = 0;
  for (const t of tries) {
    if (count > 0 && performance.now() >= deadline) break;
    const image = render(t);
    if (!image) break;
    count += 1;
    const text = await read(image, t.variant).catch(() => null);
    if (text) return { text, tries: count, variant: t.variant };
  }
  return { text: null, tries: count, variant: null };
};

const NEAR_ANGLES = [0, -3, 3, -6, 6];

/**
 * The last-chance read of a still photo (the 3rd miss): the centre of the photo,
 * where the guide put the card, at every angle; then the whole photo; then the
 * hard-card variants on the centre. About 30 tries, a couple of seconds in the
 * worker, instead of every angle x variant x region (~180).
 */
export const photoTasks = (width: number, height: number): DecodeTask[] => {
  const whole = { x: 0, y: 0, w: width, h: height };
  const centre = { x: width * 0.15, y: height * 0.15, w: width * 0.7, h: height * 0.7 };
  return [
    { region: centre, angles: SWEEP_ANGLES, variant: 'A', maxWidth: MAX_DECODE_WIDTH, minWidth: MAX_DECODE_WIDTH },
    { region: whole, angles: NEAR_ANGLES, variant: 'A', maxWidth: MAX_DECODE_WIDTH },
    { region: centre, angles: NEAR_ANGLES, variant: 'B', maxWidth: MAX_DECODE_WIDTH, minWidth: MAX_DECODE_WIDTH },
    { region: centre, angles: NEAR_ANGLES, variant: 'C', maxWidth: MAX_DECODE_WIDTH, minWidth: MAX_DECODE_WIDTH },
  ];
};
